/**
 * CJAS — language switching and Urdu right-to-left.  Jira CA-52, CA-26.
 *
 * Three languages, one of them written right to left in a script that most
 * layout code has never been tested against. The rules that matter:
 *
 *   - Norwegian is the master. A missing Norwegian string is a bug.
 *   - Urdu falls back to English, English to Norwegian. Never the reverse:
 *     showing Urdu to someone who asked for English is worse than showing
 *     Norwegian, which most of the congregation reads.
 *   - Times, dates and numbers stay left-to-right inside an RTL layout,
 *     because "17:30" reversed is a different time.
 */

const LANGS = ['no', 'en', 'ur'];
const MASTER = 'no';
const FALLBACK = { ur: ['ur', 'en', 'no'], en: ['en', 'no'], no: ['no'] };
const RTL = ['ur'];

function isRtl(lang) { return RTL.includes(lang); }
function dir(lang) { return isRtl(lang) ? 'rtl' : 'ltr'; }

/**
 * Resolve one key. Returns the value and WHICH language it came from, so the
 * UI can mark a fallback — an Urdu speaker seeing an English line should know
 * it is not yet translated rather than assume the app is broken.
 */
function t(strings, key, lang = MASTER) {
  const chain = FALLBACK[lang] || FALLBACK[MASTER];
  for (const l of chain) {
    const v = strings[l] && strings[l][key];
    if (v !== undefined && v !== null && v !== '') {
      return { text: v, lang: l, fallback: l !== lang };
    }
  }
  // Never render a raw key to a member. The key is for the developer.
  return { text: '', lang: null, fallback: true, missing: key };
}

/** What the translation review screen needs (CA-35). */
function coverage(strings) {
  const keys = Object.keys(strings[MASTER] || {});
  const out = { total: keys.length, master: MASTER };
  for (const l of LANGS) {
    const have = keys.filter(k => strings[l] && strings[l][k]);
    out[l] = { present: have.length, missing: keys.filter(k => !(strings[l] && strings[l][k])),
               percent: keys.length ? Math.round((have.length / keys.length) * 100) : 100 };
  }
  // Keys that exist in a translation but not in Norwegian are orphans — the
  // master string was renamed or deleted and the translation was left behind.
  out.orphans = LANGS.filter(l => l !== MASTER)
    .flatMap(l => Object.keys(strings[l] || {}).filter(k => !keys.includes(k)).map(k => ({ lang: l, key: k })));
  return out;
}

/**
 * Which properties mirror when the layout flips, and which must not.
 * Returning a map rather than a boolean keeps the decision in one place
 * instead of scattered `isRtl ? 'right' : 'left'` through every component.
 */
function layout(lang) {
  const rtl = isRtl(lang);
  return {
    dir: dir(lang),
    textAlign: rtl ? 'right' : 'left',
    flexDirection: rtl ? 'row-reverse' : 'row',
    // "Back" points the way you came from, which is mirrored.
    backIcon: rtl ? 'chevron-right' : 'chevron-left',
    // Progress and playback are NOT mirrored: a video scrubber running right
    // to left is confusing in every language.
    mirrorProgress: false,
    // Nastaliq is a tall script; Latin line-height clips the descenders.
    lineHeightMultiplier: rtl ? 2.1 : 1.5,
  };
}

/**
 * A time is always left-to-right. Wrapping it in the isolate characters stops
 * the bidirectional algorithm from reordering "17:30" when it sits inside an
 * Urdu sentence — which it will, and which produces a wrong time on screen
 * rather than an obvious layout glitch somebody would notice.
 */
const LRI = '⁦', FSI = '⁨', PDI = '⁩';
function isolateTime(value, lang) {
  return isRtl(lang) ? `${LRI}${value}${PDI}` : String(value);
}

/**
 * Isolate a run whose direction we do not know in advance.
 *
 * `isolateTime` forces LEFT-TO-RIGHT, which is right for a clock time —
 * "18:15" has no strong character of its own and must never be flipped.
 * It is wrong for anything that carries Urdu text: a formatted date is
 * "6. ستمبر 2026", and forcing that run LTR renders it month-first as
 * "ستمبر 2026 .6" — a date nobody in the congregation would read as the
 * sixth of September.
 *
 * FSI takes its direction from the first strong character in the run, so a
 * Latin speaker name goes LTR, an Urdu date goes RTL, and a bare number
 * inherits nothing and stays LTR. One helper, correct for all three, and it
 * still isolates — which is the part that stops a list of fragments joined
 * with "·" from interleaving.
 */
function isolate(value, lang) {
  return isRtl(lang) ? `${FSI}${value}${PDI}` : String(value);
}

/** Digits stay Western Arabic in all three languages — that is what the
 *  printed CJAS timetable uses, and the app must match the paper. */
function formatNumber(n, lang) {
  const s = new Intl.NumberFormat(lang === 'ur' ? 'en-GB' : lang === 'en' ? 'en-GB' : 'nb-NO').format(n);
  return isRtl(lang) ? isolateTime(s, lang) : s;
}

const MONTHS = {
  no: ['januar','februar','mars','april','mai','juni','juli','august','september','oktober','november','desember'],
  en: ['January','February','March','April','May','June','July','August','September','October','November','December'],
  ur: ['جنوری','فروری','مارچ','اپریل','مئی','جون','جولائی','اگست','ستمبر','اکتوبر','نومبر','دسمبر'],
};
const WEEKDAYS = {
  no: ['søndag','mandag','tirsdag','onsdag','torsdag','fredag','lørdag'],
  en: ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'],
  ur: ['اتوار','پیر','منگل','بدھ','جمعرات','جمعہ','ہفتہ'],
};

/** Written out rather than left to Intl, because Urdu month names in the
 *  browser's locale data are inconsistent and Friday matters here. */
function formatDate(date, lang = MASTER, withWeekday = true) {
  const l = LANGS.includes(lang) ? lang : MASTER;
  const d = date.getDate(), m = MONTHS[l][date.getMonth()], y = date.getFullYear();
  // The ordinal dot ("6. september") is NORWEGIAN. Urdu writes "6 ستمبر"
  // — as hijri.format() already did, so the TV and the app header put
  // "10. ستمبر" beside "27 ربیع الاول" (found on the TV event slide,
  // 2026-09-10).
  const body = l === 'no' ? `${d}. ${m} ${y}` : `${d} ${m} ${y}`;
  const out = withWeekday ? `${WEEKDAYS[l][date.getDay()]} ${body}` : body;
  return isRtl(l) ? out : out;
}

/**
 * The short month, for a calendar chip.
 *
 * The app's event card built this with `toLocaleDateString(lang === 'en' ?
 * 'en-GB' : 'nb-NO', {month:'short'})` — a hand-written locale ternary with
 * no Urdu branch, so the Urdu programme showed "SEP" beside Urdu titles.
 * `formatDate()` right above already refuses to trust Intl for exactly this
 * reason ("Urdu month names in the browser's locale data are inconsistent"),
 * and the second copy of the rule forgot both the reason and the language.
 *
 * Urdu is not abbreviated. Urdu month names are already short, and clipping
 * them to three characters produces a fragment rather than a word — the
 * reason English and Norwegian abbreviate does not apply.
 */
function monthShort(date, lang = MASTER) {
  const l = LANGS.includes(lang) ? lang : MASTER;
  const full = MONTHS[l][date.getMonth()];
  return l === 'ur' ? full : full.slice(0, 3).toUpperCase();
}

/**
 * Choosing the initial language.
 *
 * Default to Norwegian rather than the device language. Someone whose phone
 * is in English is not necessarily asking for the app in English — but
 * Norwegian is the language the mosque publishes in, and it is the one that
 * is always complete and always reviewed.
 */
function initialLang(stored, deviceLangs = []) {
  if (LANGS.includes(stored)) return stored;
  for (const d of deviceLangs) {
    const base = String(d).toLowerCase().split('-')[0];
    if (LANGS.includes(base)) return base;
  }
  return MASTER;
}

/**
 * Switching language must not lose where the member was. Returning the
 * decision explicitly stops a well-meaning implementation from resetting to
 * the home screen, which is how people lose a half-filled nikah form.
 */
function switchTo(lang, current = {}) {
  if (!LANGS.includes(lang)) return { ok: false, reason: 'unknown_language' };
  return {
    ok: true, lang, dir: dir(lang),
    keepRoute: true, keepScroll: true, keepFormState: true,
    // RN needs a restart to flip the whole layout; web does not.
    requiresRestart: isRtl(lang) !== isRtl(current.lang || MASTER),
  };
}

module.exports = {
  LANGS, MASTER, FALLBACK, RTL, MONTHS, WEEKDAYS,
  isRtl, dir, t, coverage, layout, isolateTime,
  formatNumber, formatDate, monthShort, initialLang, switchTo, isolate,
};
